import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function RefundPolicy() {
 const { t } = useTranslation();
 const navigate = useNavigate();

 useEffect(() => {
 // 滚动到页面顶部
 window.scrollTo(0, 0);
 }, []);

 return (
 <div className="min-h-screen bg-cream-100">
 {/* 顶部导航栏 */}
 <div className="bg-cream-50 shadow-sm sticky top-0 z-10">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="flex items-center justify-between h-16">
 <button
 onClick={() => navigate(-1)}
 className="flex items-center text-ink-600 hover:text-ink-900 transition-colors"
 >
 <ArrowLeft className="w-5 h-5 mr-2" />
 <span>返回</span>
 </button>
 <h1 className="text-xl font-semibold text-ink-900">Refund Policy</h1>
 <div className="w-20"></div>
 </div>
 </div>
 </div>

 {/* 主要内容 */}
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
 <div className="bg-cream-50 rounded-lg shadow-sm p-8">
 {/* 标题部分 */}
 <div className="mb-8 text-center">
 <h1 className="text-3xl font-bold text-ink-900 mb-4">Refund Policy</h1>
 <p className="text-ink-600 mb-4">
 This Refund Policy outlines the conditions and procedures for refunds on our products and services. We aim to handle refund requests fairly and transparently.
 </p>
 
 {/* 免费试用重要提示 */}
 <div className="bg-accent-50 border border-accent-200 rounded-lg p-6 max-w-3xl mx-auto">
 <h3 className="text-lg font-semibold text-accent-900 mb-2">🎯 Try Before You Buy</h3>
 <p className="text-accent-800">
 We offer <strong>free trial periods</strong> for most of our products to ensure you can fully evaluate them before making a purchase. 
 We strongly encourage all customers to take advantage of these trial opportunities to make informed decisions.
 </p>
 </div>
 </div>

 {/* 公司信息 */}
 <div className="mb-8 p-6 bg-cream-100 rounded-lg">
 <h2 className="text-xl font-semibold text-ink-900 mb-4">Company Information</h2>
 <div className="space-y-2 text-ink-700">
 <p><span className="font-medium">Company Name:</span> SmartTool AI Solutions Limited</p>
 <p><span className="font-medium">Brand Name:</span> 紫刀AI</p>
 <p><span className="font-medium">Company Website:</span> <a href="https://ai2you.org/" className="text-accent-500 hover:text-accent-600">https://ai2you.org/</a></p>
 <p><span className="font-medium">Contact Email:</span> <a href="mailto:support@ai2you.org" className="text-accent-500 hover:text-accent-600">support@ai2you.org</a></p>
 <p className="text-sm text-ink-600 mt-4">Effective Date: May 15, 2025</p>
 </div>
 </div>

 {/* 退款政策内容 */}
 <div className="space-y-8">
 {/* 1. Refund Eligibility */}
 <section>
 <h2 className="text-2xl font-semibold text-ink-900 mb-4">1. Refund Eligibility</h2>
 
 <div className="space-y-4">
 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">1.1 Subscription Services</h3>
 <p className="text-ink-700 mb-2">
 We offer refunds for our subscription services under the following conditions:
 </p>
 <ul className="list-disc list-inside space-y-1 text-ink-700 ml-4">
 <li>Request is made within 14 days of the initial subscription purchase</li>
 <li>Account has not exceeded reasonable usage limits as defined in our Terms of Service</li>
 <li>There is evidence of service malfunction that significantly impacts usability</li>
 </ul>
 </div>

 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">1.2 One-Time Purchases</h3>
 <p className="text-ink-700 mb-2">
 For one-time purchases of digital products or services:
 </p>
 <div className="bg-accent-50 border-l-4 border-accent-400 p-4 mb-4">
 <p className="text-accent-800 font-medium">
 <strong>Important Notice:</strong> One-time purchase products are generally not eligible for refunds as we provide free trial periods for you to evaluate the product before purchase.
 </p>
 </div>
 <p className="text-ink-700 mb-2">
 We encourage all customers to take advantage of our free trial offerings to fully evaluate the product before making a purchase decision. Refunds for one-time purchases may only be considered in exceptional circumstances such as:
 </p>
 <ul className="list-disc list-inside space-y-1 text-ink-700 ml-4">
 <li>Technical issues that prevent the product from functioning as advertised</li>
 <li>Significant deviation from the product description that was not apparent during the trial period</li>
 <li>Billing errors or duplicate charges</li>
 </ul>
 </div>

 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">1.3 Enterprise Solutions</h3>
 <p className="text-ink-700">
 Refunds for enterprise solutions are governed by the specific terms outlined in the enterprise agreement. General refund policies may not apply to custom enterprise solutions.
 </p>
 </div>
 </div>
 </section>

 {/* 2. Refund Process */}
 <section>
 <h2 className="text-2xl font-semibold text-ink-900 mb-4">2. Refund Process</h2>
 
 <div className="space-y-4">
 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">2.1 Refund Request Submission</h3>
 <p className="text-ink-700 mb-2">To request a refund, you must:</p>
 <ul className="list-disc list-inside space-y-1 text-ink-700 ml-4">
 <li>Submit a refund request through our customer support portal</li>
 <li>Include your account information, purchase date, and order ID</li>
 <li>Provide a detailed explanation for your refund request</li>
 <li>Submit any supporting documentation (if applicable)</li>
 <li><strong>For one-time purchases:</strong> Explain why the free trial period was insufficient to identify the issue</li>
 </ul>
 </div>

 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">2.2 Review Period</h3>
 <p className="text-ink-700">
 Our team will review your refund request within 5 business days. We may contact you for additional information during the review process.
 </p>
 </div>

 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">2.3 Refund Processing</h3>
 <p className="text-ink-700 mb-2">If your refund request is approved:</p>
 <ul className="list-disc list-inside space-y-1 text-ink-700 ml-4">
 <li>The refund will be processed using the original payment method when possible</li>
 <li>Processing time typically takes 3-10 business days, depending on your payment provider</li>
 <li>You will receive a confirmation email once the refund has been processed</li>
 </ul>
 </div>
 </div>
 </section>

 {/* 3. Non-Refundable Items */}
 <section>
 <h2 className="text-2xl font-semibold text-ink-900 mb-4">3. Non-Refundable Items</h2>
 <p className="text-ink-700 mb-4">
 The following items and situations are generally not eligible for refunds:
 </p>
 <ul className="list-disc list-inside space-y-2 text-ink-700 ml-4">
 <li>One-time purchase products (free trials are provided for evaluation before purchase)</li>
 <li>Subscription plans after the 14-day refund period has expired</li>
 <li>Usage-based services that have been partially or fully consumed</li>
 <li>Custom development or implementation services that have been delivered according to specifications</li>
 <li>Services where terms of use have been violated</li>
 <li>Add-on features or credits that have been partially or fully used</li>
 <li>Purchases made using promotional credits or gift cards</li>
 </ul>
 <p className="text-ink-700 mt-4">
 We reserve the right to review refund requests for these items on a case-by-case basis.
 </p>
 </section>

 {/* 4. Partial Refunds */}
 <section>
 <h2 className="text-2xl font-semibold text-ink-900 mb-4">4. Partial Refunds</h2>
 <p className="text-ink-700 mb-4">
 In certain situations, we may offer partial refunds:
 </p>
 
 <div className="space-y-4">
 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">4.1 Prorated Refunds</h3>
 <p className="text-ink-700">
 For subscription services, if a refund is approved outside the full refund period, we may offer a prorated refund based on the unused portion of the service period.
 </p>
 </div>

 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">4.2 Service Quality Issues</h3>
 <p className="text-ink-700">
 If our service experiences significant downtime or performance issues that impact your usage, we may provide partial refunds proportional to the affected period.
 </p>
 </div>

 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">4.3 One-Time Product Exceptions</h3>
 <p className="text-ink-700">
 For one-time purchase products, partial refunds are extremely rare and only considered in cases of technical malfunction or billing errors. Since free trials are available, customers are expected to fully evaluate products before purchase.
 </p>
 </div>
 </div>
 
 <p className="text-ink-700 mt-4">
 The calculation method for partial refunds will be clearly communicated during the refund process.
 </p>
 </section>

 {/* 5. Dispute Resolution */}
 <section>
 <h2 className="text-2xl font-semibold text-ink-900 mb-4">5. Dispute Resolution</h2>
 <p className="text-ink-700 mb-4">
 If you disagree with the outcome of your refund request:
 </p>
 
 <div className="space-y-4">
 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">5.1 Dispute Process</h3>
 <p className="text-ink-700">
 You may submit a dispute by contacting our dedicated refund resolution team at <a href="mailto:support@ai2you.org" className="text-accent-500 hover:text-accent-600">support@ai2you.org</a> within 10 business days of receiving our decision.
 </p>
 </div>

 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">5.2 Required Information</h3>
 <p className="text-ink-700 mb-2">Your dispute should include:</p>
 <ul className="list-disc list-inside space-y-1 text-ink-700 ml-4">
 <li>Your original refund request reference number</li>
 <li>Detailed explanation of why you believe the decision should be reconsidered</li>
 <li>Any additional evidence supporting your case</li>
 </ul>
 </div>

 <div>
 <h3 className="text-lg font-medium text-ink-800 mb-2">5.3 Dispute Review</h3>
 <p className="text-ink-700">
 Disputes will be reviewed by a senior member of our customer service team who was not involved in the original decision. We aim to resolve disputes within 15 business days.
 </p>
 </div>
 </div>
 </section>

 {/* 6. Policy Changes */}
 <section>
 <h2 className="text-2xl font-semibold text-ink-900 mb-4">6. Policy Changes</h2>
 <p className="text-ink-700 mb-4">
 We reserve the right to modify this Refund Policy at any time. Changes to our Refund Policy will be posted on our website, and the revised policy will become effective upon posting.
 </p>
 <p className="text-ink-700 mb-4">
 For purchases made prior to a policy change, the refund policy in effect at the time of purchase will generally apply. However, if the new policy provides more favorable terms to customers, we may apply the updated policy at our discretion.
 </p>
 <p className="text-ink-700">
 We encourage you to review our Refund Policy periodically for any changes.
 </p>
 </section>

 {/* 7. Contact Information */}
 <section>
 <h2 className="text-2xl font-semibold text-ink-900 mb-4">7. Contact Information</h2>
 <p className="text-ink-700 mb-4">
 If you have any questions about our Refund Policy, please contact us at:
 </p>
 
 <div className="bg-cream-100 p-6 rounded-lg">
 <div className="space-y-2 text-ink-700">
 <p><span className="font-medium">Email:</span> <a href="mailto:support@ai2you.org" className="text-accent-500 hover:text-accent-600">support@ai2you.org</a></p>
 <p><span className="font-medium">Mailing Address:</span> Hong Kong Kowloon Garden Street 2-16, Haokeng Commercial Center, Room 502C</p>
 <p className="text-sm text-ink-600 mt-4">
 Our customer support team is available Monday through Friday, 9:00 AM to 5:00 PM (UTC+8).
 </p>
 </div>
 </div>
 </section>

 {/* 最后更新时间 */}
 <div className="text-center pt-8 border-t border-ink-200">
 <p className="text-sm text-ink-500">Last Updated: May 15, 2025</p>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}